import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { setClosingConfirmation } from '../lib/telegram';

/**
 * Шапка, главная кнопка, шторка и тост живут в оболочке, а наполняет их экран.
 * Прототип делал это через глобальные `titleEl`/`mainBtn`/`openSheet`; здесь то же
 * самое, только экран объявляет своё через хуки, а оболочка рисует.
 *
 * Действия и состояние разнесены по двум контекстам намеренно: действия обязаны
 * иметь постоянную ссылку, иначе эффект экрана переустанавливается на каждый
 * рендер оболочки и обновления зацикливаются.
 */

export type MainButton = {
  label: string;
  disabled?: boolean;
  onClick: () => void;
};

/**
 * Тост с кнопкой — единственный способ отменить то, что сделано без диалога:
 * скрытие и удаление пустого направления происходят сразу, а «Вернуть» живёт
 * ровно столько, сколько висит тост.
 */
export type ToastAction = { label: string; onClick: () => void };
export type Toast = { text: string; action?: ToastAction };

type ShellState = {
  title: string;
  main: MainButton | null;
  sheet: ReactNode | null;
  toast: Toast | null;
};

/**
 * Кнопка помнит, какой экран её поставил: у соседних шагов надписи совпадают
 * («Далее» → «Далее»), и без хозяина оболочка удержала бы обработчик предыдущего
 * экрана — нажатие ушло бы не туда.
 */
type OwnedButton = MainButton & { owner: object };

/**
 * Сторож ухода с экрана. Форма с несохранёнными правками ставит его, оболочка
 * спрашивает перед каждым переключением вкладки и перед «назад»: экран не может
 * перехватить чужую кнопку сам, а терять заполненное молча — нельзя.
 */
export type LeaveGuard = () => Promise<boolean>;

type ShellActions = {
  setScreen: (title: string, main: OwnedButton | null) => void;
  openSheet: (node: ReactNode) => void;
  closeSheet: () => void;
  toast: (message: string, opts?: { action?: ToastAction; ms?: number }) => void;
  setGuard: (guard: LeaveGuard | null) => void;
  askBeforeLeaving: () => Promise<boolean>;
};

const StateContext = createContext<ShellState | null>(null);
const ActionsContext = createContext<ShellActions | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState('');
  const [main, setMain] = useState<OwnedButton | null>(null);
  const [sheet, setSheet] = useState<ReactNode | null>(null);
  const [toastValue, setToastValue] = useState<Toast | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const guard = useRef<LeaveGuard | null>(null);

  const setScreen = useCallback((nextTitle: string, nextMain: OwnedButton | null) => {
    setTitle(nextTitle);
    setMain((prev) => (sameButton(prev, nextMain) ? prev : nextMain));
  }, []);

  const openSheet = useCallback((node: ReactNode) => setSheet(node), []);
  const closeSheet = useCallback(() => setSheet(null), []);

  const setGuard = useCallback((next: LeaveGuard | null) => { guard.current = next; }, []);
  const askBeforeLeaving = useCallback(() => (guard.current ? guard.current() : Promise.resolve(true)), []);

  const toast = useCallback((message: string, opts?: { action?: ToastAction; ms?: number }) => {
    // С кнопкой тост висит дольше: за две секунды «Вернуть» не успеть прочитать и нажать.
    const ms = opts?.ms ?? (opts?.action ? 10_000 : 1900);
    setToastValue({
      text: message,
      action: opts?.action && {
        label: opts.action.label,
        onClick: () => { setToastValue(null); opts.action!.onClick(); },
      },
    });
    clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastValue(null), ms);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const actions = useMemo<ShellActions>(
    () => ({ setScreen, openSheet, closeSheet, toast, setGuard, askBeforeLeaving }),
    [setScreen, openSheet, closeSheet, toast, setGuard, askBeforeLeaving],
  );
  const state = useMemo<ShellState>(
    () => ({ title, main, sheet, toast: toastValue }),
    [title, main, sheet, toastValue],
  );

  return (
    <ActionsContext.Provider value={actions}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </ActionsContext.Provider>
  );
}

/**
 * Кнопка пересобирается на каждом рендере экрана — сравниваем по содержимому,
 * но только в пределах одного экрана: чужую кнопку с той же надписью подменять
 * обязательно, иначе нажатие вызовет обработчик предыдущего шага.
 */
function sameButton(a: OwnedButton | null, b: OwnedButton | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.owner === b.owner && a.label === b.label && !!a.disabled === !!b.disabled;
}

function useActions(): ShellActions {
  const ctx = useContext(ActionsContext);
  if (!ctx) throw new Error('ShellProvider отсутствует выше по дереву');
  return ctx;
}

export function useShellState(): ShellState {
  const ctx = useContext(StateContext);
  if (!ctx) throw new Error('ShellProvider отсутствует выше по дереву');
  return ctx;
}

/**
 * Экран объявляет заголовок и главную кнопку. Обработчик берётся из ref, поэтому
 * пересоздание стрелки в теле экрана не считается изменением кнопки.
 */
export function useScreen(title: string, main?: MainButton | null): void {
  const { setScreen } = useActions();
  const label = main?.label ?? '';
  const disabled = main?.disabled ?? false;
  const handler = useRef<(() => void) | undefined>(undefined);
  handler.current = main?.onClick;
  const owner = useRef({});

  useEffect(() => {
    setScreen(title, label
      ? { label, disabled, owner: owner.current, onClick: () => handler.current?.() }
      : null);
  }, [title, label, disabled, setScreen]);
}

export function useSheet(): { open: (node: ReactNode) => void; close: () => void } {
  const { openSheet, closeSheet } = useActions();
  return useMemo(() => ({ open: openSheet, close: closeSheet }), [openSheet, closeSheet]);
}

export function useToast(): (message: string, opts?: { action?: ToastAction; ms?: number }) => void {
  return useActions().toast;
}

export function useShellActions(): ShellActions {
  return useActions();
}

/**
 * Пока `dirty` — оболочка спрашивает `ask()` перед уходом с экрана, а Telegram
 * переспрашивает при закрытии Mini App. Снимается вместе с флагом и при
 * размонтировании: чужой экран не должен унаследовать сторожа.
 */
export function useLeaveGuard(dirty: boolean, ask: LeaveGuard): void {
  const { setGuard } = useActions();
  const handler = useRef(ask);
  handler.current = ask;

  useEffect(() => {
    if (!dirty) return;
    setGuard(() => handler.current());
    setClosingConfirmation(true);
    const onUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onUnload);
    return () => {
      setGuard(null);
      setClosingConfirmation(false);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [dirty, setGuard]);
}
